<template>
  <div
    class="flex flex-col w-52 group template-watchfaceitem outline outline-[2px] outline-offset-0 rounded-2xl"
    :class="{
      'outline-[#0D84F2]': selected,
      'outline-[transparent]': !selected
    }"
    :data-id="template.id"
  >
    <!-- 预览图 -->
    <div
      class="flex items-center justify-center border-box overflow-hidden w-52 h-52 bg-[#404040] rounded-2xl border-[2px] border-solid border-[transparent] group-hover:border-[#0D84F2]"
    >
      <img
        :src="template.preview"
        class="border border-solid border-[#C9C9C9]"
        :style="{
          height: '160px',
          width: size.width * scale + 'px',
          borderRadius: radius,
          background: '#000000'
        }"
      />
    </div>
    <div
      class="flex items-center text-white w-52 overflow-hidden rounded-b-xl pt-2 px-2.5"
    >
      <div class="flex flex-col w-full overflow-hidden">
        <div class="text-lg leading-none">
          <EditableTitle
            ref="editableTitleRef"
            :tree-id="template.id"
            :title="template.name"
            class="h-8 text-lg"
            :submit="rename"
            :valid="validName"
            :highlight-text="inputSearchValue"
          />
        </div>
      </div>
      <DropDown
        :trigger="'click'"
        :menus="contextmenus(template)"
        class="flex items-center"
      >
        <Icon
          class="opacity-0 group-hover:opacity-100"
          icon="nav.svg"
          :width="20"
          :height="20"
          fill="transparent"
          @dblclick.stop
        />
      </DropDown>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, watch } from 'vue';
import { getWatchfaceDeviceRadius, useWatchfaceDevices } from '@/hooks';
import 'dayjs/locale/zh-cn';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  TemplateService,
  WatchfaceTemplateItem
} from '@/services/template.service';
const { wholeDevices } = useWatchfaceDevices();
const templateService = new TemplateService();
dayjs.extend(relativeTime);

const emit = defineEmits<{
  (event: 'rename-complete'): void;
}>();
const props = defineProps({
  template: {
    type: Object as PropType<WatchfaceTemplateItem>,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  },
  contextmenus: {
    type: Function,
    default: () => {
      return;
    }
  },
  inputSearchValue: {
    type: String,
    default: undefined
  }
});
const template = ref(props.template);
watch(
  () => props.template,
  (newTemplate) => {
    template.value = newTemplate;
  }
);

const size = computed(() => {
  const _size = wholeDevices.value
    .find((item) => item.deviceType === props.template.deviceType)
    ?.size.split('x');
  const width = +(_size?.[0] ?? 0);
  const height = +(_size?.[1] ?? 0);
  return { width, height };
});

const scale = computed(() => (size.value.height ? 160 / size.value.height : 1));

const radius = computed(() => {
  const val =
    getWatchfaceDeviceRadius(props.template.deviceType ?? 'circle') *
    scale.value;
  return val + 'px';
});

const validName = (id: string, newName: string) => {
  if (!newName.length || !newName.trim()) return '名称不可为空';
  else if (!/^[^.].*/.test(newName)) return '表盘名称不能以 . 开头';
  else if (!/^[^\\/:*?"<>|]*$/.test(newName ?? ''))
    return '表盘名称不能有以下字符\\/:*?"<>|';

  let chineseCharNum = 0,
    otherCharNum = 0;
  for (let i = 0; i < newName.length; i++) {
    var c = newName.charAt(i);
    //基本汉字
    if (c.match(/[\u4e00-\u9fa5]/)) {
      chineseCharNum++;
    }
    //基本汉字补充
    else if (c.match(/[\u9FA6-\u9fcb]/)) {
      chineseCharNum++;
    } else {
      otherCharNum++;
    }
  }
  let totalCharNum = chineseCharNum * 3 + otherCharNum;
  if (totalCharNum > 40) {
    return '最多40个字符,汉字不超过13个';
  }
};

const rename = async function (path: string, val: string) {
  if (val.trim() === props.template.name) return;
  await templateService.updateTemplate(path, {
    name: val.trim()
  });
  const templats = await templateService.readTemplates();
  const foundTemplate = (templats as WatchfaceTemplateItem[]).find(
    (item) => item.id === props.template.id
  );
  if (foundTemplate) {
    template.value = foundTemplate;
  }
  emit('rename-complete');
};
const editableTitleRef = ref();
const startEditMode = () => {
  editableTitleRef.value.startEditMode();
};
defineExpose({
  startEditMode,
  id: template.value.id
});
</script>
<style scoped></style>
